import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NodeApi, TreeApi } from 'react-arborist';
import { BundleTreeType, BundleTreeTypes, SectionsTreeNode, TREE_ROW_HEIGHT } from '@repo/atomic-components';

export interface StickyAncestorPin<T extends SectionsTreeNode> {
  node: NodeApi<T>;
  level: number;
  top: number;
}

// Only these ancestor types pin — a Cluster or Document doesn't have children,
// so nothing scrolls inside it and there's no reason to keep it visible.
const PINNABLE_TYPES = new Set<BundleTreeType>([
  BundleTreeTypes.PROVIDER,
  BundleTreeTypes.CATEGORY,
  BundleTreeTypes.SUB_CATEGORY,
]);

const STICK_TO_BY_TYPE: Partial<Record<BundleTreeType, number>> = {
  [BundleTreeTypes.PROVIDER]: 0,
  [BundleTreeTypes.CATEGORY]: TREE_ROW_HEIGHT,
  [BundleTreeTypes.SUB_CATEGORY]: TREE_ROW_HEIGHT * 2,
};

const PIN_ZONE_HEIGHT = TREE_ROW_HEIGHT * (PINNABLE_TYPES.size + 1);

interface UseStickyAncestorPinsOptions<T extends SectionsTreeNode> {
  treeApiRef: React.RefObject<TreeApi<T> | null>;
  // A value that changes whenever the tree data (or open-state via the
  // consumer's callbacks) shifts, so the hook can recompute pins.
  invalidateKey: unknown;
}

interface UseStickyAncestorPinsResult<T extends SectionsTreeNode> {
  pins: StickyAncestorPin<T>[];
  scrollbarWidth: number;
  overlayRef: React.RefObject<HTMLDivElement | null>;
  // Call this after imperatively opening/closing nodes to refresh pins on
  // the next frame (react-arborist's visibleNodes settles asynchronously).
  scheduleRecompute: () => void;
}

export const useStickyAncestorPins = <T extends SectionsTreeNode>({
  treeApiRef,
  invalidateKey,
}: UseStickyAncestorPinsOptions<T>): UseStickyAncestorPinsResult<T> => {
  const [pins, setPins] = useState<StickyAncestorPin<T>[]>([]);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const recompute = useCallback(() => {
    const tree = treeApiRef.current;
    const scrollEl = tree?.listEl.current;
    if (!tree || !scrollEl) {
      setPins([]);
      return;
    }

    const scrollTop = scrollEl.scrollTop;
    const nextScrollbarWidth = scrollEl.offsetWidth - scrollEl.clientWidth;
    setScrollbarWidth((prev) => (prev === nextScrollbarWidth ? prev : nextScrollbarWidth));

    const active = new Map<BundleTreeType, { node: NodeApi<T>; top: number }>();

    for (let i = 0; i < tree.visibleNodes.length; i++) {
      const n = tree.visibleNodes[i];
      const rowTop = i * TREE_ROW_HEIGHT;
      const rowOffset = rowTop - scrollTop;
      if (rowOffset > PIN_ZONE_HEIGHT) break;

      // Any row at the same or shallower tree level than a currently pinned
      // section acts as its eviction boundary — the pinned subtree has
      // ended. Push the pin upward as the boundary approaches, then clear
      // it once the boundary reaches the stick-to line.
      for (const [type, current] of Array.from(active.entries())) {
        if (current.node === n) continue;
        if (n.level > current.node.level) continue;
        const boundaryStickTo = STICK_TO_BY_TYPE[type];
        if (boundaryStickTo === undefined) continue;
        if (rowOffset <= boundaryStickTo) {
          active.delete(type);
        } else if (rowOffset < boundaryStickTo + TREE_ROW_HEIGHT) {
          current.top = Math.min(current.top, rowOffset - TREE_ROW_HEIGHT);
        }
      }

      const stickTo = STICK_TO_BY_TYPE[n.data.type];
      if (stickTo === undefined) continue;
      if (rowOffset > stickTo) continue;
      if (n.isOpen) {
        active.set(n.data.type, { node: n, top: stickTo });
      }
    }

    setPins(
      Array.from(active.values()).map(({ node, top }) => ({
        node,
        level: node.level,
        top,
      })),
    );
  }, [treeApiRef]);

  const scheduleRecompute = useCallback(() => {
    requestAnimationFrame(() => recompute());
  }, [recompute]);

  // Attach scroll + resize listeners once the tree's virtual list is
  // mounted. A ResizeObserver on the scroll element covers container
  // resizes and virtualisation changes with one code path.
  useEffect(() => {
    let scrollEl: HTMLDivElement | null = null;
    let observer: ResizeObserver | null = null;
    let rafId = 0;
    const onScroll = () => recompute();

    const attach = () => {
      const el = treeApiRef.current?.listEl.current;
      if (el) {
        scrollEl = el;
        el.addEventListener('scroll', onScroll, { passive: true });
        observer = new ResizeObserver(() => recompute());
        observer.observe(el);
        recompute();
      } else {
        rafId = requestAnimationFrame(attach);
      }
    };
    attach();

    return () => {
      cancelAnimationFrame(rafId);
      scrollEl?.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [recompute]);

  // Data / open-state changes shift which rows are visible.
  useEffect(() => {
    recompute();
  }, [invalidateKey, recompute]);

  // While a native HTML5 drag is in flight, hide the overlay imperatively
  // (visibility hidden + pointer-events none) so react-arborist's rows
  // underneath receive dragover/drop events. Toggling via a ref instead of
  // state avoids a mid-drag re-render, which disrupts drop-target detection.
  useEffect(() => {
    const applyDragState = (dragging: boolean) => {
      const el = overlayRef.current;
      if (!el) return;
      el.style.visibility = dragging ? 'hidden' : 'visible';
      el.style.pointerEvents = dragging ? 'none' : '';
    };
    const onDragStart = () => applyDragState(true);
    const onDragEnd = () => applyDragState(false);
    document.addEventListener('dragstart', onDragStart);
    document.addEventListener('dragend', onDragEnd);
    document.addEventListener('drop', onDragEnd);
    return () => {
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('dragend', onDragEnd);
      document.removeEventListener('drop', onDragEnd);
    };
  }, []);

  return useMemo(
    () => ({ pins, scrollbarWidth, overlayRef, scheduleRecompute }),
    [pins, scrollbarWidth, scheduleRecompute],
  );
};
